import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../utils/auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const RECORD_TYPE_LABELS = {
  project_billing: 'Project Billing',
  contract: 'Contract',
  commitment: 'Commitment',
  budget: 'Budget',
  ap_invoice: 'AP Invoice',
  labor_cost: 'Labor Cost',
  project_expense: 'Project Expense',
};

const RECORD_TYPE_COLORS = {
  project_billing: 'bg-indigo-100 text-indigo-800',
  contract: 'bg-blue-100 text-blue-800',
  commitment: 'bg-purple-100 text-purple-800',
  budget: 'bg-teal-100 text-teal-800',
  ap_invoice: 'bg-orange-100 text-orange-800',
  labor_cost: 'bg-rose-100 text-rose-800',
  project_expense: 'bg-amber-100 text-amber-800',
};

const PRIMARY_BUTTON =
  'btn-claret';
const ROW_BUTTON =
  'btn-claret';
function formatMoney(value) {
  return `$${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatIntegrationError(error) {
  const data = error.response?.data || {};
  const parts = [];
  if (data.procore_http_status) {
    parts.push(`Procore HTTP ${data.procore_http_status}`);
  }
  if (data.error) {
    parts.push(data.error);
  }
  if (data.procore_response_body_snippet && !parts.join('\n').includes(data.procore_response_body_snippet)) {
    parts.push(data.procore_response_body_snippet);
  }
  if (data.details && typeof data.details === 'string' && !parts.join('\n').includes(data.details)) {
    parts.push(data.details);
  }
  return parts.filter(Boolean).join('\n') || error.message || 'Failed to send to Procore';
}

const ProcoreSyncReport = ({ initialProjectVuid }) => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [projects, setProjects] = useState([]);
  const [integrations, setIntegrations] = useState([]);

  const [selectedRecordType, setSelectedRecordType] = useState('project_billing');
  const [selectedProject, setSelectedProject] = useState(initialProjectVuid || 'all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('not_synced');
  const [selectedIntegration, setSelectedIntegration] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [sortConfig, setSortConfig] = useState({ key: 'project', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingIds, setSendingIds] = useState({});
  const [sendResults, setSendResults] = useState([]);

  const fetchReportRequestIdRef = useRef(0);

  const fetchFilterOptions = useCallback(async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [projectsRes, periodsRes, integrationsRes] = await Promise.all([
        axios.get(`${baseURL}/api/projects`, { headers }),
        axios.get(`${baseURL}/api/accounting-periods`, { headers }),
        axios.get(`${baseURL}/api/integrations`, { headers }),
      ]);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
      setAccountingPeriods(Array.isArray(periodsRes.data) ? periodsRes.data : []);
      const procore = (integrationsRes.data || []).filter(
        (item) => item.status === 'active' && item.integration_type === 'procore'
      );
      setIntegrations(procore);
      if (procore.length === 1) {
        setSelectedIntegration((current) => (current === 'all' ? procore[0].vuid : current));
      }
    } catch (err) {
      console.error('Error loading Procore sync report filters:', err);
    }
  }, []);

  const fetchReportData = useCallback(async () => {
    const requestId = ++fetchReportRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const params = {};
      if (selectedRecordType && selectedRecordType !== 'all') {
        params.record_type = selectedRecordType;
      }
      if (selectedProject && selectedProject !== 'all') {
        params.project_vuid = selectedProject;
      }
      if (selectedPeriod && selectedPeriod !== 'all') {
        params.accounting_period_vuid = selectedPeriod;
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (selectedIntegration && selectedIntegration !== 'all') {
        params.integration_vuid = selectedIntegration;
      }
      const response = await axios.get(`${baseURL}/api/procore-sync-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (requestId !== fetchReportRequestIdRef.current) {
        return;
      }
      setRecords(response.data.records || []);
      setSummary(response.data.summary || null);
      setSelectedIds(new Set());
    } catch (err) {
      if (requestId !== fetchReportRequestIdRef.current) {
        return;
      }
      console.error('Error fetching Procore sync report:', err);
      setError(err.response?.data?.error || 'Failed to fetch Procore sync report');
    } finally {
      if (requestId === fetchReportRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedRecordType, selectedProject, selectedPeriod, selectedStatus, selectedIntegration]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const filteredRecords = records.filter((record) => {
    if (!searchTerm) {
      return true;
    }
    const term = searchTerm.toLowerCase();
    const projectLabel = record.project
      ? `${record.project.project_number} ${record.project.project_name}`
      : '';
    return (
      (record.reference_number || '').toLowerCase().includes(term) ||
      (record.description || '').toLowerCase().includes(term) ||
      projectLabel.toLowerCase().includes(term) ||
      (RECORD_TYPE_LABELS[record.record_type] || '').toLowerCase().includes(term)
    );
  });

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    let aVal;
    let bVal;
    if (sortConfig.key === 'project') {
      aVal = a.project ? `${a.project.project_number} ${a.project.project_name}` : '';
      bVal = b.project ? `${b.project.project_number} ${b.project.project_name}` : '';
    } else if (sortConfig.key === 'accounting_period') {
      aVal = a.accounting_period ? a.accounting_period.year * 100 + a.accounting_period.month : 0;
      bVal = b.accounting_period ? b.accounting_period.year * 100 + b.accounting_period.month : 0;
      return (aVal - bVal) * direction;
    } else if (sortConfig.key === 'total_amount') {
      return ((a.total_amount || 0) - (b.total_amount || 0)) * direction;
    } else {
      aVal = a[sortConfig.key] || '';
      bVal = b[sortConfig.key] || '';
    }
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return -1 * direction;
    if (aVal > bVal) return 1 * direction;
    return 0;
  });

  const sendableRecords = sortedRecords.filter((record) => !record.is_synced && record.send_action);
  const allSelected = sendableRecords.length > 0 && sendableRecords.every((record) => selectedIds.has(record.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sendableRecords.map((record) => record.id)));
  };

  const toggleSelect = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const sendRecord = async (record) => {
    if (!record.send_action) {
      return { ok: false, error: 'This record cannot be sent to Procore' };
    }
    const token = getToken();
    try {
      const response = await axios.post(
        `${baseURL}/api${record.send_action.path}`,
        record.send_action.body,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data && response.data.success === false) {
        return { ok: false, error: response.data.error || 'Procore rejected the send' };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatIntegrationError(err) };
    }
  };

  const handleSend = async (recordsToSend) => {
    if (!recordsToSend.length) {
      return;
    }
    setError(null);
    const results = [];
    for (const record of recordsToSend) {
      setSendingIds((current) => ({ ...current, [record.id]: true }));
      const result = await sendRecord(record);
      results.push({
        id: record.id,
        reference_number: record.reference_number,
        record_type: record.record_type,
        ok: result.ok,
        error: result.error,
      });
      setSendingIds((current) => {
        const next = { ...current };
        delete next[record.id];
        return next;
      });
    }
    setSendResults(results);
    await fetchReportData();
    const failed = results.filter((item) => !item.ok);
    if (failed.length) {
      setError(
        failed
          .map((item) => `${RECORD_TYPE_LABELS[item.record_type] || item.record_type} ${item.reference_number}: ${item.error}`)
          .join('\n')
      );
    }
  };

  const sendingAny = Object.keys(sendingIds).length > 0;
  const selectedSendable = sendableRecords.filter((record) => selectedIds.has(record.id));

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Procore Sync Report</h1>
        <p className="text-gray-600">
          Find records on Procore-linked projects that have not been sent to Procore, then send them from here.
          Cost transactions (AP invoices, labor, and expenses) are sent as Direct Costs after they have been sent to accounting.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Record Type</label>
            <select
              value={selectedRecordType}
              onChange={(e) => setSelectedRecordType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="project_billing">Project Billing</option>
              <option value="contract">Contract</option>
              <option value="commitment">Commitment</option>
              <option value="budget">Budget</option>
              <option value="ap_invoice">AP Invoice</option>
              <option value="labor_cost">Labor Cost</option>
              <option value="project_expense">Project Expense</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.vuid} value={project.vuid}>
                  {project.project_number} - {project.project_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Accounting Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Periods</option>
              {accountingPeriods.map((period) => (
                <option key={period.vuid} value={period.vuid}>
                  {period.month}/{period.year} ({period.status})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sync Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="not_synced">Not in Procore</option>
              <option value="synced">Already in Procore</option>
              <option value="all">All</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Integration</label>
            <select
              value={selectedIntegration}
              onChange={(e) => setSelectedIntegration(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Procore Integrations</option>
              {integrations.map((integration) => (
                <option key={integration.vuid} value={integration.vuid}>
                  {integration.integration_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Procore-linked projects</div>
              <div className="text-2xl font-semibold text-gray-900">{summary.mapped_project_count || 0}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-sm text-amber-700">Not in Procore</div>
              <div className="text-2xl font-semibold text-amber-800">{summary.not_synced || 0}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-700">Already in Procore</div>
              <div className="text-2xl font-semibold text-green-800">{summary.synced || 0}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-700">Showing</div>
              <div className="text-2xl font-semibold text-blue-800">{sortedRecords.length}</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-4 mt-4">
          <p className="text-sm text-gray-500">
            Only records on projects already mapped to Procore are listed. AP invoices, labor costs, and project expenses send as Direct Costs after accounting sync.
          </p>
          <button
            type="button"
            onClick={() => handleSend(selectedSendable)}
            disabled={sendingAny || selectedSendable.length === 0}
            className={PRIMARY_BUTTON}
          >
            {sendingAny ? 'Sending…' : `Send selected to Procore${selectedSendable.length ? ` (${selectedSendable.length})` : ''}`}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-800 whitespace-pre-wrap">{error}</div>
        </div>
      )}

      {sendResults.length > 0 && !error && sendResults.every((item) => item.ok) && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="text-sm text-green-800">
            Sent {sendResults.length} record{sendResults.length === 1 ? '' : 's'} to Procore.
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading Procore sync report...</div>
        </div>
      )}

      {!loading && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={sendableRecords.length === 0}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('record_type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Record Type</span>
                      {getSortIcon('record_type')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('reference_number')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Reference</span>
                      {getSortIcon('reference_number')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('project')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project</span>
                      {getSortIcon('project')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('accounting_period')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Period</span>
                      {getSortIcon('accounting_period')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_amount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount</span>
                      {getSortIcon('total_amount')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Procore
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRecords.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No records found matching the selected filters.
                    </td>
                  </tr>
                ) : (
                  sortedRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(record.id)}
                          disabled={record.is_synced || !record.send_action}
                          onChange={() => toggleSelect(record.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${RECORD_TYPE_COLORS[record.record_type] || 'bg-gray-100 text-gray-800'}`}>
                          {RECORD_TYPE_LABELS[record.record_type] || record.record_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">{record.reference_number || 'N/A'}</div>
                        {record.description ? (
                          <div className="text-xs text-gray-500 max-w-xs truncate">{record.description}</div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.project ? (
                          <Link to={record.detail_path || `/projects/${record.project.vuid}`} className="text-blue-700 hover:underline">
                            {record.project.project_number} - {record.project.project_name}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.accounting_period
                          ? `${record.accounting_period.month}/${record.accounting_period.year}`
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMoney(record.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.record_status || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.is_synced ? (
                          <div>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              In Procore
                            </span>
                            {record.external_id ? (
                              <div className="text-xs text-gray-500 mt-1">ID {record.external_id}</div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            Not sent
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.is_synced ? (
                          <span className="text-sm text-gray-400">Already sent</span>
                        ) : record.send_blocked_reason ? (
                          <span className="text-sm text-gray-500">{record.send_blocked_reason}</span>
                        ) : (
                          <button
                            type="button"
                            className={ROW_BUTTON}
                            disabled={sendingAny}
                            onClick={() => handleSend([record])}
                          >
                            {sendingIds[record.id] ? 'Sending…' : 'Send to Procore'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcoreSyncReport;
